import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BookmarkPlus, DollarSign, Minus, Plus, PlusCircle, BookmarkCheck } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { useCollection } from '../hooks/useCollection';
import { useTranslation } from '../hooks/useTranslation';
import { useUserStore } from '../hooks/useUser';
import Spinner from './Spinner';
import { DropdownMenuItem } from './ui/dropdown-menu';

export default function AddToCollectionButton({ card, variant = 'button', buttonVariant = 'outline', buttonText, className = '', asDropdownItem = false, showQuantityInButton = true, dialogOpen, onDialogClose }) {
  const { t } = useTranslation();
  const { user } = useUserStore();
  const { addToCollection, getQuantityInCollection } = useCollection();

  const [internalDialogOpen, setInternalDialogOpen] = useState(false);

  // Use either the controlled or uncontrolled dialog state
  const isDialogOpen = dialogOpen !== undefined ? dialogOpen : internalDialogOpen;

  // Handle dialog close from parent or internal state
  const handleDialogClose = (open) => {
    if (!open) {
      if (onDialogClose) {
        onDialogClose();
      } else {
        setInternalDialogOpen(false);
      }
    }
  };
  const [quantity, setQuantity] = useState(1);
  const [condition, setCondition] = useState('NM');
  const [language, setLanguage] = useState('English');
  const [finish, setFinish] = useState('Non-Foil');
  const [purchasePrice, setPurchasePrice] = useState('');
  const [isProxy, setIsProxy] = useState(false);
  const [isAdding, setIsAdding] = useState(false);

  const collectionQuantity = getQuantityInCollection(card?.id);
  console.log(collectionQuantity);

  const handleOpenModal = () => {
    if (!user) {
      toast.error(t('common.loginRequired'));
      return;
    }
    if (onDialogClose) {
      // For controlled dialog
      // We don't set it directly, just notify the parent
    } else {
      // For uncontrolled dialog
      setInternalDialogOpen(true);
    }
  };

  // Handle adding card to collection
  const handleAddToCollection = async () => {
    if (!user) {
      toast.error(t('common.loginRequired'));
      return;
    }

    if (!card) {
      toast.error(t('collection.noCardSelected'));
      return;
    }

    setIsAdding(true);
    try {
      // Ensure card has required properties
      if (!card.id || !card.name) {
        toast.error('Card is missing required properties');
        console.error('Card missing required properties:', card);
        return;
      }

      // Prepare the data to be sent
      const collectionData = {
        card,
        quantity: parseInt(quantity) || 1,
        condition,
        language,
        finish,
        isProxy,
      };

      // Only add purchasePrice if it has a value
      if (purchasePrice) {
        collectionData.purchasePrice = parseFloat(purchasePrice);
      }

      const result = await addToCollection.mutateAsync(collectionData);

      // Reset form
      setQuantity(1);
      setCondition('NM');
      setLanguage('English');
      setFinish('Non-Foil');
      setPurchasePrice('');
      setIsProxy(false);

      // Close dialog
      if (onDialogClose) {
        onDialogClose();
      } else {
        setInternalDialogOpen(false);
      }
    } catch (error) {
      console.error('Error adding card to collection:', error);

      // Provide a user-friendly error message
      let errorMessage = 'Failed to add card to collection';

      // Check for specific error types
      if (error.message && error.message.includes('Unsupported field value')) {
        errorMessage = 'Invalid data format. Please try again.';
      } else if (error.message) {
        // Use the error message but don't expose implementation details
        errorMessage = error.message.replace(/Function setDoc\(\) called with invalid data.*/, 'Invalid data format');
      }

      toast.error(errorMessage);
    } finally {
      setIsAdding(false);
    }
  };

  // Determine button text based on collection quantity
  const getButtonText = () => {
    if (buttonText) return buttonText;

    if (collectionQuantity > 0) {
      return `${t('collection.inCollection')} (${collectionQuantity})`;
    }
    return t('collection.addToCollection');
  };

  // Render as dropdown item if requested
  if (asDropdownItem) {
    return (
      <>
        <DropdownMenuItem onClick={handleOpenModal} disabled={isAdding} className={collectionQuantity > 0 ? 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/50' : ''}>
          {collectionQuantity > 0 ? <BookmarkCheck className='w-4 h-4 mr-2' /> : <BookmarkPlus className='w-4 h-4 mr-2' />}
          {getButtonText()}
        </DropdownMenuItem>

        <CollectionDialog
          open={isDialogOpen}
          setOpen={handleDialogClose}
          card={card}
          quantity={quantity}
          setQuantity={setQuantity}
          condition={condition}
          setCondition={setCondition}
          language={language}
          setLanguage={setLanguage}
          finish={finish}
          setFinish={setFinish}
          purchasePrice={purchasePrice}
          setPurchasePrice={setPurchasePrice}
          isProxy={isProxy}
          setIsProxy={setIsProxy}
          isAdding={isAdding}
          handleAddToCollection={handleAddToCollection}
          collectionQuantity={collectionQuantity}
        />
      </>
    );
  }

  // Render as regular button
  return (
    <>
      <Button variant={collectionQuantity > 0 ? 'darkGray' : buttonVariant} className={className} onClick={handleOpenModal} disabled={isAdding}>
        {collectionQuantity > 0 ? <BookmarkCheck className='w-4 h-4 mr-2' /> : <BookmarkPlus className='w-4 h-4 mr-2' />}
        {getButtonText()}
      </Button>

      <CollectionDialog
        open={isDialogOpen}
        setOpen={handleDialogClose}
        card={card}
        quantity={quantity}
        setQuantity={setQuantity}
        condition={condition}
        setCondition={setCondition}
        language={language}
        setLanguage={setLanguage}
        finish={finish}
        setFinish={setFinish}
        purchasePrice={purchasePrice}
        setPurchasePrice={setPurchasePrice}
        isProxy={isProxy}
        setIsProxy={setIsProxy}
        isAdding={isAdding}
        handleAddToCollection={handleAddToCollection}
        collectionQuantity={collectionQuantity}
      />
    </>
  );
}

// Extracted dialog component to be reused
function CollectionDialog({ open, setOpen, card, quantity, setQuantity, condition, setCondition, language, setLanguage, finish, setFinish, purchasePrice, setPurchasePrice, isProxy, setIsProxy, isAdding, handleAddToCollection, collectionQuantity }) {
  const { t } = useTranslation();

  if (!card) return null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className='w-[95vw] max-w-[500px] sm:w-full p-6'>
        <DialogHeader className='mb-4'>
          <DialogTitle className='text-xl'>{t('collection.addCardToCollection')}</DialogTitle>
          <DialogDescription className='text-muted-foreground'>{t('collection.addCardDescription')}</DialogDescription>
        </DialogHeader>

        <div className='flex flex-col sm:flex-row gap-6'>
          <div className='w-full sm:w-1/3 mx-auto sm:mx-0' style={{ maxWidth: '200px' }}>
            <div className='rounded-lg overflow-hidden border border-border shadow-md'>
              {card.image_uris?.normal ? (
                <img src={card.image_uris.normal} alt={card.name} className='w-full' />
              ) : card.card_faces && card.card_faces[0]?.image_uris?.normal ? (
                <img src={card.card_faces[0].image_uris.normal} alt={card.name} className='w-full' />
              ) : null}
            </div>

            {collectionQuantity > 0 && (
              <div className='mt-3 text-center bg-primary/5 rounded-md py-2 px-3'>
                <span className='text-sm text-muted-foreground'>{t('collection.inCollection')}:</span>
                <span className='ml-1 font-medium text-foreground'>{collectionQuantity}</span>
              </div>
            )}
          </div>
          <div className='w-full sm:w-2/3'>
            <h3 className='text-xl font-medium text-foreground mb-1'>{card.name}</h3>
            <p className='text-sm text-muted-foreground mb-5'>
              {card.set_name} ({card.set.toUpperCase()})
            </p>

            <div className='grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4'>
              <div className='space-y-2'>
                <label className='text-sm font-medium text-foreground block'>{t('collection.quantity')}</label>
                <div className='flex items-center'>
                  <Button type='button' variant='outline' size='icon' className='h-9 w-9 rounded-r-none' onClick={() => setQuantity(Math.max(1, parseInt(quantity) - 1))} disabled={parseInt(quantity) <= 1}>
                    <Minus className='h-4 w-4' />
                  </Button>
                  <Input type='number' min='1' value={quantity} onChange={(e) => setQuantity(e.target.value)} className='h-9 rounded-none text-center bg-background border-x-0 border-border text-foreground' />
                  <Button type='button' variant='outline' size='icon' className='h-9 w-9 rounded-l-none' onClick={() => setQuantity(parseInt(quantity) + 1)}>
                    <Plus className='h-4 w-4' />
                  </Button>
                </div>
              </div>

              <div className='space-y-2'>
                <label className='text-sm font-medium text-foreground block'>Condition</label>
                <Select value={condition} onValueChange={setCondition}>
                  <SelectTrigger>
                    <SelectValue placeholder='Select condition' />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value='M'>M</SelectItem>
                    <SelectItem value='NM'>NM</SelectItem>
                    <SelectItem value='SP'>SP</SelectItem>
                    <SelectItem value='MP'>MP</SelectItem>
                    <SelectItem value='HP'>HP</SelectItem>
                    <SelectItem value='DMG'>DMG</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className='space-y-2'>
                <label className='text-sm font-medium text-foreground block'>Language</label>
                <Select value={language} onValueChange={setLanguage}>
                  <SelectTrigger>
                    <SelectValue placeholder='Select language' />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value='English'>English</SelectItem>
                    <SelectItem value='Spanish'>Spanish</SelectItem>
                    <SelectItem value='French'>French</SelectItem>
                    <SelectItem value='German'>German</SelectItem>
                    <SelectItem value='Italian'>Italian</SelectItem>
                    <SelectItem value='Portuguese'>Portuguese</SelectItem>
                    <SelectItem value='Japanese'>Japanese</SelectItem>
                    <SelectItem value='Korean'>Korean</SelectItem>
                    <SelectItem value='Russian'>Russian</SelectItem>
                    <SelectItem value='Simplified Chinese'>Simplified Chinese</SelectItem>
                    <SelectItem value='Traditional Chinese'>Traditional Chinese</SelectItem>
                    <SelectItem value='Hebrew'>Hebrew</SelectItem>
                    <SelectItem value='Latin'>Latin</SelectItem>
                    <SelectItem value='Ancient Greek'>Ancient Greek</SelectItem>
                    <SelectItem value='Arabic'>Arabic</SelectItem>
                    <SelectItem value='Sanskrit'>Sanskrit</SelectItem>
                    <SelectItem value='Phyrexian'>Phyrexian</SelectItem>
                    <SelectItem value='Quenya'>Quenya</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className='space-y-2'>
                <label className='text-sm font-medium text-foreground block'>Finish</label>
                <Select value={finish} onValueChange={setFinish}>
                  <SelectTrigger>
                    <SelectValue placeholder='Select finish' />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value='Non-Foil'>Non-Foil</SelectItem>
                    <SelectItem value='Foil'>Foil</SelectItem>
                    <SelectItem value='Etched'>Etched</SelectItem>
                    <SelectItem value='Textured'>Textured</SelectItem>
                    <SelectItem value='Gilded'>Gilded</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className='space-y-2'>
                <label className='text-sm font-medium text-foreground block'>Purchase Price</label>
                <div className='relative'>
                  <div className='absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none'>
                    <DollarSign className='h-4 w-4 text-muted-foreground' />
                  </div>
                  <Input type='number' step='0.01' min='0' value={purchasePrice} onChange={(e) => setPurchasePrice(e.target.value)} className='pl-8' placeholder='0.00' />
                </div>
              </div>

              <div className='flex items-center space-x-2 mt-4'>
                <Checkbox id='isProxy' checked={isProxy} onCheckedChange={setIsProxy} />
                <Label htmlFor='isProxy' className='text-sm font-medium cursor-pointer'>
                  This is a proxy
                </Label>
              </div>
            </div>

            {card.prices?.usd && (
              <div className='mt-5 bg-accent/30 rounded-md py-2 px-3'>
                <span className='text-sm text-muted-foreground'>{t('collection.price')}:</span>
                <span className='font-medium text-foreground ml-1'>${card.prices.usd}</span>
              </div>
            )}
          </div>
        </div>

        <DialogFooter className='flex flex-col sm:flex-row gap-2 sm:gap-0 mt-6'>
          <Button variant='outline' onClick={() => setOpen(false)} className='w-full sm:w-auto order-2 sm:order-1'>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleAddToCollection} disabled={isAdding} className='w-full sm:w-auto order-1 sm:order-2 gap-2'>
            {isAdding ? (
              <>
                <Spinner dimensionClasses='w-4 h-4' />
                <span>{t('common.adding')}</span>
              </>
            ) : (
              <>
                <PlusCircle className='h-4 w-4' />
                <span>{t('collection.addToCollection')}</span>
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
